import type {
  AudienceBaseDescriptor,
  AudienceBaseType,
  DistributionCategoryDescriptor,
  DistributionQuestionDescriptor,
  DistributionsCatalog,
} from "@/interfaces/v2Analytics";
import { resolveLabel } from "@/utils/catalogLabels";

/**
 * The selection resolved and validated against the catalog.
 *
 * `familyKey` is the dimension's identity: `questionCode` is not unique within
 * a category, so it names the question but not which of its dimensions.
 */
export interface ResolvedDistributionSelection {
  categoryKey: string;
  questionCode: string;
  audienceBase: AudienceBaseType;
  familyKey: string;
}

/** Display names for module-linked categories (catalog moduleKey → label). */
const MODULE_DISPLAY_LABELS: Record<string, string> = {
  EQUITY: "Equity",
  CONVERSION: "Conversion",
  REPUTATION: "Reputation",
  VISIBILITY: "Visibility",
  IMAGERY: "Emotional Imagery",
  ADVERTISING: "Advertising",
};

/**
 * FE module token (screenConfig.module — uppercase MetricModuleType) →
 * distribution-catalog `moduleKey` (== ClientAppModule.moduleKey, lowercase).
 * Most are just the lowercased token; VISIBILITY and IMAGERY use distinct
 * backend keys (mirrors the brand-visibility alias in bootstrapApi.ts).
 */
const MODULE_TO_CATALOG_KEY: Record<string, string> = {
  EQUITY: "equity",
  CONVERSION: "conversion",
  REPUTATION: "reputation",
  VISIBILITY: "brand-visibility",
  IMAGERY: "emotional",
  ADVERTISING: "advertising",
};

/**
 * A module name reduced to one comparable token.
 *
 * The same module is spelled two ways: `userSelection.module` is the lowercase
 * route segment (`emotional`), `screenConfig.module` is the FE token
 * (`IMAGERY`). Comparing them raw reports a difference that is not one.
 */
export function normaliseModuleToken(module: string | null | undefined): string {
  const upper = module?.toUpperCase() ?? "";
  return upper === "IMAGERY" ? "EMOTIONAL" : upper;
}

/** Reverse: catalog moduleKey → FE module token (uppercase). */
const CATALOG_KEY_TO_MODULE: Record<string, string> = Object.fromEntries(
  Object.entries(MODULE_TO_CATALOG_KEY).map(([m, c]) => [c, m]),
);

/**
 * Warn when a catalog arrives carrying no applicability at all.
 *
 * Deny-by-default is correct, but it must not fail silently. The bootstrap
 * response is JDK-serialized into Redis under `bootstrap_v1_unified` with a
 * pinned serialVersionUID, so a stale cache entry deserializes *successfully*
 * with every `marketIds` null and the picker renders empty with no error
 * anywhere. That is a missed cache flush at deploy, not a data problem, and it
 * looks identical to "this client has no distributions".
 *
 * Logs rather than throws: an absent `marketIds` is legitimate on its own.
 */
export function warnIfCatalogLacksApplicability(
  catalog: DistributionsCatalog | null,
): void {
  if (!import.meta.env.DEV || !catalog) return;
  const dimensions = catalog.categories.flatMap((c) => c.questions ?? []);
  if (dimensions.length === 0) return;
  if (dimensions.some((q) => q.marketIds?.length)) return;
  console.error(
    `[distributionsCatalog] all ${dimensions.length} dimensions arrived without ` +
      `marketIds — every one will be filtered out. Expected cause: a stale ` +
      `bootstrap_v1_unified Redis entry served from before the market-scoped ` +
      `catalog shipped. Flush that cache.`,
  );
}

/**
 * Ordering comparator. `sequence` alone, deliberately: the backend already
 * orders with a `sequence → label → familyKey` tiebreak, and `Array.sort` is
 * stable (ES2019+), so sorting on `sequence` alone preserves that tiebreak for
 * ties. An FE tiebreak would undo it.
 *
 * The `?? 0` is a null-guard, not a tiebreak: `sequence` is a Java boxed
 * `Integer` under `@JsonInclude(NON_NULL)`, so it is omitted rather than
 * nulled, and `undefined - undefined` is NaN — for which comparator behaviour
 * is unspecified.
 */
const bySequence = (a: { sequence: number }, b: { sequence: number }) =>
  (a.sequence ?? 0) - (b.sequence ?? 0);

/**
 * Categories visible on a module's Profile view: the module's own category
 * first, then the ad-hoc (null moduleKey) ones, each by sequence.
 *
 * The module's category leads because it is the subject of the screen, and
 * because being first makes it the default — `resolveDistributionSelection`
 * falls back to `[0]`. Catalog `sequence` cannot express that on its own: it is
 * a single global order, and the ad-hoc Demographics sits at 1, so sorting by
 * sequence alone puts Demographics first on every screen and buries Visibility
 * (17) and Reputation (18) behind all three ad-hoc categories.
 */
export function getCategoriesForModule(
  catalog: DistributionsCatalog | null,
  module: string,
): DistributionCategoryDescriptor[] {
  if (!catalog) return [];
  const catalogKey =
    MODULE_TO_CATALOG_KEY[module.toUpperCase()] ?? module.toLowerCase();
  const visible = catalog.categories.filter(
    (c) => c.moduleKey == null || c.moduleKey.toLowerCase() === catalogKey,
  );
  return [
    ...visible.filter((c) => c.moduleKey != null).sort(bySequence),
    ...visible.filter((c) => c.moduleKey == null).sort(bySequence),
  ];
}

/**
 * Is this dimension offered in every selected market?
 *
 * The two guards are asymmetric on purpose. An empty `marketIds` on the
 * descriptor is a *server assertion* — applicability is deny-by-default, so
 * "offered nowhere" means hide it. An empty *selection* is the absence of a
 * question: the catalog lands before the market selection does (the backfill in
 * selectionListener runs off an effect gated on `isDataFetched`, which is
 * dispatched after the catalog), and an unselected brand can leave the market
 * pool empty indefinitely. Gating on that window would make
 * `hasDistributionsForModule` false and downgrade live `?visualizationType=DISTRIBUTION`
 * deep links.
 *
 * Collapsing the two into the obvious "either empty ⇒ false" is what breaks
 * those deep links. Do not.
 */
function isDimensionAvailable(
  q: DistributionQuestionDescriptor,
  marketIds: number[],
): boolean {
  if (marketIds.length === 0) return true; // scope UNKNOWN — do not gate
  if (!q.marketIds?.length) return false; // scope KNOWN, offered nowhere — deny
  return marketIds.every((m) => q.marketIds!.includes(m));
}

/**
 * The dimensions of a category available in the selected markets, in catalog
 * order. `marketIds` is mandatory: a default would let a caller silently take
 * the ungated path, which is exactly how the filter came to be a no-op.
 */
export function getQuestionsForCategory(
  catalog: DistributionsCatalog | null,
  categoryKey: string | null,
  marketIds: number[],
): DistributionQuestionDescriptor[] {
  if (!catalog || !categoryKey) return [];
  const category = catalog.categories.find((c) => c.key === categoryKey);
  return (category?.questions ?? [])
    .slice()
    .sort(bySequence)
    .filter((q) => isDimensionAvailable(q, marketIds));
}

/**
 * Categories the user can actually pick for the given module *and* market(s):
 * `getCategoriesForModule` filtered to those retaining at least one
 * market-valid question. Single choke point for "selectable for this market"
 * — the gate, Metric dropdown, and resolver all define selectability through
 * this, so they can never disagree.
 */
export function getSelectableCategoriesForModule(
  catalog: DistributionsCatalog | null,
  module: string,
  marketIds: number[],
): DistributionCategoryDescriptor[] {
  return getCategoriesForModule(catalog, module).filter(
    (c) => getQuestionsForCategory(catalog, c.key, marketIds).length > 0,
  );
}

export function getAudienceBases(
  catalog: DistributionsCatalog | null,
): AudienceBaseDescriptor[] {
  return (catalog?.audienceBases ?? []).slice().sort(bySequence);
}

/**
 * Picker label for a dimension, in the selected market's words.
 *
 * `marketId` is the resolved label market (`labelMarketId(markets)`) — the
 * selected market when exactly one is selected, otherwise undefined, which
 * resolves to the canonical default.
 */
export function getQuestionDisplayLabel(
  q: DistributionQuestionDescriptor,
  marketId: number | undefined,
): string {
  return resolveLabel(q, marketId);
}

/**
 * Picker label for a category, in the selected market's words.
 *
 * The catalog is authoritative: it is the only thing that can express a market
 * renaming the category (Etsy's "Brand Health" for Equity). MODULE_DISPLAY_LABELS
 * survives only as a fallback for a blank catalog label.
 */
export function getCategoryDisplayLabel(
  c: DistributionCategoryDescriptor,
  marketId: number | undefined,
): string {
  const resolved = resolveLabel(c, marketId);
  if (resolved && resolved.trim() !== "") return resolved;

  const feToken = c.moduleKey
    ? CATALOG_KEY_TO_MODULE[c.moduleKey.toLowerCase()]
    : undefined;
  return (feToken && MODULE_DISPLAY_LABELS[feToken]) || c.label;
}

/**
 * Validates a (possibly stale, legacy-numeric, or legacy-questionCode)
 * selection against the catalog. Any unresolvable part falls back to defaults:
 * first category → its first dimension → the dimension's defaultAudienceBase;
 * a base outside the dimension's supportedAudienceBases snaps to the default.
 * Returns null when the catalog is absent or has no categories for the module.
 *
 * `profileResponse` now carries `familyKey`. The questionCode step exists for
 * URLs and saved views written before that change: it lands on the first
 * dimension of the right question rather than on an unrelated one. Safe because
 * family keys are unique within a category.
 */
export function resolveDistributionSelection(
  catalog: DistributionsCatalog | null,
  module: string,
  selection: {
    profileMetric: string | null;
    profileResponse: string | null;
    profileGroup: string | null;
  },
  marketIds: number[],
): ResolvedDistributionSelection | null {
  // Only categories that actually have a market-valid question can be
  // selected/rendered — a category naming a question-less (or out-of-market)
  // category, or the whole module resolving to none, must fall through to a
  // real category, not a dead end.
  const categoriesWithQuestions = getSelectableCategoriesForModule(
    catalog,
    module,
    marketIds,
  );
  if (categoriesWithQuestions.length === 0) return null;

  const category =
    categoriesWithQuestions.find((c) => c.key === selection.profileMetric) ??
    categoriesWithQuestions[0];
  const questions = getQuestionsForCategory(catalog, category.key, marketIds);
  const question =
    questions.find((q) => q.familyKey === selection.profileResponse) ??
    questions.find((q) => q.questionCode === selection.profileResponse) ??
    questions[0];
  const resolvedDefault = question.supportedAudienceBases.includes(
    question.defaultAudienceBase,
  )
    ? question.defaultAudienceBase
    : question.supportedAudienceBases[0];
  const audienceBase = question.supportedAudienceBases.includes(
    selection.profileGroup as AudienceBaseType,
  )
    ? (selection.profileGroup as AudienceBaseType)
    : resolvedDefault;

  return {
    categoryKey: category.key,
    questionCode: question.questionCode,
    audienceBase,
    familyKey: question.familyKey,
  };
}

/**
 * The store/URL shape of a resolved selection — what `userSelection`'s
 * `profileMetric` / `profileResponse` / `profileGroup` hold, and therefore what
 * `resolveDistributionSelection` reads back on the next render.
 *
 * `profileResponse` carries **`familyKey`**, not `questionCode`. Writing the
 * questionCode here would be silently lossy: a category can hold many
 * dimensions of one question, so reading it back resolves first-match-wins and
 * snaps the user off whichever dimension they picked. Write-back is defined
 * here alone so the read and write halves cannot diverge.
 */
export function toStoredSelection(selection: ResolvedDistributionSelection): {
  profileMetric: string;
  profileResponse: string;
  profileGroup: AudienceBaseType;
} {
  return {
    profileMetric: selection.categoryKey,
    profileResponse: selection.familyKey,
    profileGroup: selection.audienceBase,
  };
}

/** True when the module has at least one category with questions — i.e. the Profile view can render. */
export function hasDistributionsForModule(
  catalog: DistributionsCatalog | null,
  module: string,
  marketIds: number[],
): boolean {
  return getSelectableCategoriesForModule(catalog, module, marketIds).length > 0;
}

/**
 * Normalized (uppercase) FE module token of the catalog category with the
 * given key — matches the `screenConfig.module` / MetricModuleType vocabulary
 * and the date-bounds config keys; null for ad-hoc or unknown categories.
 */
export function getModuleKeyForCategory(
  catalog: DistributionsCatalog | null,
  categoryKey: string | null,
): string | null {
  if (!catalog || !categoryKey) return null;
  const category = catalog.categories.find((c) => c.key === categoryKey);
  const raw = category?.moduleKey;
  if (!raw) return null;
  return CATALOG_KEY_TO_MODULE[raw.toLowerCase()] ?? raw.toUpperCase();
}
